/**
 * Samadhan Center — Full Photo & Video Gallery Page
 * Standalone /samadhan/gallery route
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { ArrowLeft, Image as ImageIcon, Video as VideoIcon, X, Heart, Flame } from 'lucide-react';
import api from '../../utils/api';

const FALLBACK_IMG = 'https://customer-assets.emergentagent.com/job_94259c42-41bd-46e6-b640-f415dc05ba06/artifacts/uxz9qvbs_SAMADHAN_CENTER_peace_mind_celeb%E2%80%A6_202607111933.jpeg';

// localStorage key for per-device like dedupe
const LIKED_KEY = 'samadhan_liked_ids_v1';
const readLiked = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || '[]')); }
  catch { return new Set(); }
};
const writeLiked = (set) => {
  try { localStorage.setItem(LIKED_KEY, JSON.stringify([...set])); } catch { /* noop */ }
};

const isVideoUrl = (u) => /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(u || '') || /youtu\.?be|vimeo/i.test(u || '');

// YouTube thumbnail helper — extracts video ID from any URL variant
const getYouTubeId = (u = '') => {
  const m = u.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
};
// YouTube embed URL builder — handles watch?v= AND youtu.be/xxx formats
const getYouTubeEmbedUrl = (u = '') => {
  const id = getYouTubeId(u);
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : u;
};

const SamadhanGallery = () => {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('photos');
  const [zone, setZone] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [liked, setLiked] = useState(() => readLiked());

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/samadhan/gallery');
        setItems(r.data.images || []);
      } catch { toast.error('Gallery load failed'); }
    })();
  }, []);

  const handleLike = async (e, item) => {
    e.stopPropagation();
    if (liked.has(item.id)) { toast('Aap already like kar chuke ho', { icon: '❤️' }); return; }
    // Optimistic update
    const next = new Set(liked); next.add(item.id); setLiked(next); writeLiked(next);
    setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, likes: (x.likes || 0) + 1 } : x));
    try {
      const r = await api.post(`/samadhan/gallery/${item.id}/like`);
      // Sync with authoritative count
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, likes: r.data.likes } : x));
    } catch {
      // Rollback on failure
      const roll = new Set(liked); roll.delete(item.id); setLiked(roll); writeLiked(roll);
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, likes: Math.max(0, (x.likes || 1) - 1) } : x));
      toast.error('Like failed');
    }
  };

  // 🔥 Trending = top 3 by likes (min 1 like)
  const trending = useMemo(() => {
    return [...items]
      .filter((x) => x?.image_url && (x.likes || 0) >= 1)
      .sort((a, b) => (b.likes || 0) - (a.likes || 0))
      .slice(0, 3);
  }, [items]);

  const filtered = useMemo(() => {
    let list = items.filter((x) => x && x.image_url);
    if (tab === 'photos') list = list.filter((x) => !isVideoUrl(x.image_url));
    if (tab === 'videos') list = list.filter((x) => isVideoUrl(x.image_url));
    if (zone !== 'all') list = list.filter((x) => (x.zone || 'general') === zone);
    return list;
  }, [items, tab, zone]);

  const photos = items.filter((x) => x?.image_url && !isVideoUrl(x.image_url));
  const videos = items.filter((x) => x?.image_url && isVideoUrl(x.image_url));

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-950 text-white" data-testid="samadhan-gallery-page">
      <Helmet>
        <title>Samadhan Center · Photo & Video Gallery · The Dream Project</title>
        <meta name="description" content="Explore the vision of SAMADHAN CENTER — wellness, entertainment & leisure. Community-funded dream project opening 01/01/2031, Paratwada." />
        <meta property="og:image" content={FALLBACK_IMG} />
      </Helmet>

      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/samadhan" data-testid="gallery-back-btn" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-black">॥ Samadhan Center ॥</div>
              <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-fuchsia-300 via-amber-200 to-emerald-300 bg-clip-text text-transparent">🖼️ Photo & Video Gallery</h1>
              <div className="text-xs text-slate-400 mt-0.5">The Dream Project · Paratwada, Maharashtra</div>
            </div>
          </div>
          <div className="flex gap-1 bg-white/5 rounded-lg p-1 border border-white/10">
            <button data-testid="gallery-tab-photos" onClick={() => setTab('photos')} className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 ${tab === 'photos' ? 'bg-fuchsia-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}>
              <ImageIcon className="w-3 h-3" /> Photos ({photos.length})
            </button>
            <button data-testid="gallery-tab-videos" onClick={() => setTab('videos')} className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 ${tab === 'videos' ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}>
              <VideoIcon className="w-3 h-3" /> Videos ({videos.length})
            </button>
            <button data-testid="gallery-tab-all" onClick={() => setTab('all')} className={`px-3 py-1.5 rounded-md text-xs font-bold ${tab === 'all' ? 'bg-amber-500 text-black' : 'text-slate-300 hover:bg-white/5'}`}>All ({items.length})</button>
          </div>
        </div>

        {/* Zone filter */}
        <div className="flex flex-wrap gap-1.5">
          {['all', 'wellness', 'entertainment', 'leisure', 'general'].map((z) => (
            <button key={z} onClick={() => setZone(z)} className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize border ${zone === z ? 'border-amber-400 bg-amber-500/20 text-amber-200' : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'}`} data-testid={`gallery-zone-${z}`}>
              {z === 'all' ? '🌟 All Zones' : z === 'wellness' ? '🌿 Wellness' : z === 'entertainment' ? '🎉 Entertainment' : z === 'leisure' ? '🌴 Leisure' : '📌 General'}
            </button>
          ))}
        </div>

        {/* 🔥 Trending — top 3 by likes */}
        {trending.length > 0 && (
          <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-orange-950/30 via-slate-900 to-slate-950 p-3" data-testid="gallery-trending-section">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-4 h-4 text-orange-400 animate-pulse" />
              <h3 className="text-sm font-black bg-gradient-to-r from-orange-300 to-amber-200 bg-clip-text text-transparent uppercase tracking-widest">🔥 Trending Now · Top {trending.length}</h3>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {trending.map((im, idx) => {
                const isVid = isVideoUrl(im.image_url);
                const ytId = isVid ? getYouTubeId(im.image_url) : null;
                return (
                  <div
                    key={`trend-${im.id}`}
                    onClick={() => setLightbox(im)}
                    className="relative aspect-video rounded-lg overflow-hidden border border-amber-400/30 bg-black/40 cursor-pointer group"
                    data-testid={`gallery-trending-item-${idx}`}
                  >
                    {ytId ? (
                      <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" />
                    ) : isVid ? (
                      <video src={im.image_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <img src={im.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <div className="absolute top-1 left-1 text-[10px] font-black px-1.5 py-0.5 rounded bg-orange-500 text-white">
                      #{idx + 1}
                    </div>
                    <div className="absolute bottom-1 right-1 flex items-center gap-0.5 text-[10px] font-black px-1.5 py-0.5 rounded-full bg-rose-500 text-white">
                      <Heart className="w-2.5 h-2.5 fill-white" />
                      {im.likes || 0}
                    </div>
                    {isVid && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                          <div className="w-0 h-0 border-l-[9px] border-l-slate-900 border-y-[6px] border-y-transparent ml-0.5" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-white/10 py-16 text-center">
            <div className="text-4xl mb-2 opacity-40">🖼️</div>
            <div className="text-slate-400 text-sm">Koi {tab === 'videos' ? 'video' : tab === 'photos' ? 'photo' : 'content'} nahi mila.</div>
            <div className="text-slate-500 text-[11px] mt-1">Admin panel mein add karein.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map((im) => {
              const isVid = isVideoUrl(im.image_url);
              return (
                <div key={im.id} className="rounded-xl border border-white/10 overflow-hidden group bg-slate-900/50 hover:border-amber-400 transition cursor-pointer" onClick={() => setLightbox(im)} data-testid={`gallery-item-${im.id}`}>
                  <div className="relative aspect-square bg-black/40">
                    {isVid ? (
                      (() => {
                        const ytId = getYouTubeId(im.image_url);
                        return (
                          <>
                            {ytId ? (
                              <img
                                src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                                alt={im.title || 'video'}
                                loading="lazy"
                                className="w-full h-full object-cover group-hover:scale-105 transition"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <video
                                src={im.image_url}
                                muted
                                playsInline
                                preload="metadata"
                                className="w-full h-full object-cover"
                              />
                            )}
                            {/* ▶ Play overlay */}
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none">
                              <div className="w-14 h-14 rounded-full bg-white/95 group-hover:bg-emerald-400 flex items-center justify-center shadow-xl transition transform group-hover:scale-110">
                                <div className="w-0 h-0 border-l-[16px] border-l-slate-900 border-y-[10px] border-y-transparent ml-1" />
                              </div>
                            </div>
                            <div className="absolute bottom-1 right-1 text-[9px] px-1.5 py-0.5 rounded bg-black/80 text-white font-black uppercase tracking-wider">
                              {ytId ? '▶ YT' : '▶ Video'}
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      <img
                        src={im.image_url}
                        alt={im.title || 'Samadhan'}
                        loading="lazy"
                        onError={(e) => {
                          // Show clear broken-image state (don't loop into fallback)
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent && !parent.querySelector('.broken-img-badge')) {
                            const div = document.createElement('div');
                            div.className = 'broken-img-badge absolute inset-0 flex flex-col items-center justify-center bg-red-950/40 border-2 border-dashed border-red-400/50 text-center p-2';
                            div.innerHTML = '<div class="text-2xl mb-1">⚠️</div><div class="text-[10px] font-black text-red-300 uppercase tracking-widest">Image Broken</div><div class="text-[9px] text-slate-400 mt-1 break-all">URL not loading</div>';
                            parent.appendChild(div);
                          }
                        }}
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                      />
                    )}
                    <div className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/70 text-amber-200 uppercase font-bold border border-amber-400/40">
                      {im.zone || 'general'}
                    </div>
                    {/* ❤️ Like button */}
                    <button
                      onClick={(e) => handleLike(e, im)}
                      data-testid={`gallery-like-${im.id}`}
                      title={liked.has(im.id) ? 'Already liked' : 'Like this'}
                      className={`absolute top-1 right-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-black border transition ${liked.has(im.id) ? 'bg-rose-500/90 border-rose-300 text-white' : 'bg-black/60 border-white/20 text-white hover:bg-rose-500/60'}`}
                    >
                      <Heart className={`w-3 h-3 ${liked.has(im.id) ? 'fill-white' : ''}`} />
                      {im.likes || 0}
                    </button>
                  </div>
                  {(im.title || im.caption) && (
                    <div className="p-1.5">
                      {im.title && <div className="text-[11px] text-white font-semibold truncate">{im.title}</div>}
                      {im.caption && <div className="text-[10px] text-slate-400 truncate">{im.caption}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"><X className="w-5 h-5" /></button>
          <div className="max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {isVideoUrl(lightbox.image_url) ? (
              /youtu\.?be/i.test(lightbox.image_url) ? (
                <iframe
                  src={getYouTubeEmbedUrl(lightbox.image_url)}
                  className="w-full aspect-video rounded-xl bg-black"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  title="YouTube video"
                  data-testid="gallery-lightbox-youtube"
                />
              ) : /vimeo/i.test(lightbox.image_url) ? (
                <iframe
                  src={lightbox.image_url.replace(/vimeo\.com\/(\d+)/, 'player.vimeo.com/video/$1') + '?autoplay=1'}
                  className="w-full aspect-video rounded-xl bg-black"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  title="Vimeo video"
                />
              ) : (
                <video
                  src={lightbox.image_url}
                  controls autoPlay playsInline
                  className="w-full max-h-[80vh] rounded-xl bg-black"
                  data-testid="gallery-lightbox-video"
                />
              )
            ) : (
              <img src={lightbox.image_url} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} alt={lightbox.title || 'Samadhan'} className="w-full max-h-[80vh] object-contain rounded-xl" />
            )}
            {(lightbox.title || lightbox.caption) && (
              <div className="mt-3 text-center">
                {lightbox.title && <div className="text-lg font-bold text-white">{lightbox.title}</div>}
                {lightbox.caption && <div className="text-sm text-slate-300">{lightbox.caption}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SamadhanGallery;
